import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Typography,
    Paper
} from '@mui/material';
import { Search, Folder, Undo, AdminPanelSettings, Visibility, Clear } from '@mui/icons-material';
import { array, bool, func, string } from 'prop-types';

/**
 * @component FolderPermissionsTable
 * 
 * Table component for folder permissions management.
 * - Toolbar with 5 buttons: Search + All Admin + All View + Undo + Clear All
 * - 2-column layout (Folder Name | Permission)
 * - Fixed table scrolling
 * - Subtle hover effect on rows
 * - Sticky header for scrolling through hundreds of folders
 * - Radio button interface per row (None/View/Admin)
 * 
 * @param {Object} props
 * @param {Array} props.folders - Array of folder objects with permissions
 * @param {boolean} props.loading - Whether data is loading
 * @param {string} props.searchTerm - Current search term
 * @param {function} props.onSearchChange - Callback for search term changes
 * @param {function} props.onPermissionChange - Callback for permission changes
 * @param {function} props.onAllAdmin - All Admin button handler
 * @param {function} props.onAllView - All View button handler
 * @param {function} props.onClearAll - Clear all permissions handler
 * @param {function} props.onUndo - Undo handler
 * @param {boolean} props.canUndo - Whether undo is available
 * @param {string} props.mode - "create" or "edit" mode
 * @returns {JSX.Element}
 */
function FolderPermissionsTable({ 
    folders, 
    loading, 
    searchTerm, 
    onSearchChange,
    onPermissionChange,
    onAllAdmin,
    onAllView,
    onClearAll,
    onUndo,
    canUndo,
    mode 
}) {
    if (loading) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="300px"
                flexDirection="column"
                gap={2}
            >
                <CircularProgress size={40} />
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Toolbar - 5 Functionalities */}
            <Toolbar sx={{ 
                px: 3, 
                py: 2, 
                gap: 2,
                borderBottom: 1,
                borderColor: 'divider',
                minHeight: 'auto !important'
            }}>
                {/* Search Field */}
                <TextField
                    variant="outlined"
                    placeholder="Search folders..."
                    size="small"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    sx={{ flexGrow: 1, maxWidth: 300 }}
                />
                {/* All Admin Button */}
                <Button
                    variant="contained"
                    color="warning"
                    onClick={onAllAdmin}
                    startIcon={<AdminPanelSettings />}
                    sx={{ 
                        fontWeight: 600,
                        textTransform: 'none',
                        minWidth: 120
                    }}
                >
                    All Admin
                </Button>
                {/* All View Button */}
                <Button
                    variant="outlined"
                    color="info"
                    onClick={onAllView}
                    startIcon={<Visibility />}
                    sx={{ 
                        fontWeight: 500,
                        textTransform: 'none',
                        minWidth: 110
                    }}
                >
                    All View
                </Button>
                {/* Undo Button */}
                <Button 
                    variant="text" 
                    color="inherit"
                    onClick={onUndo}
                    disabled={!canUndo}
                    startIcon={<Undo />}
                    sx={{ 
                        fontWeight: 500,
                        textTransform: 'none',
                        color: canUndo ? 'text.primary' : 'text.disabled'
                    }}
                >
                    Undo
                </Button>
                {/* Clear All Button */}
                <Button 
                    variant="outlined" 
                    color="error"
                    onClick={onClearAll}
                    startIcon={<Clear />}
                    sx={{ 
                        fontWeight: 500,
                        textTransform: 'none',
                        minWidth: 110
                    }}
                >
                    Clear All
                </Button>
            </Toolbar>

            {/* 2-Column Table */}
            <TableContainer 
                component={Paper} 
                elevation={0}
                sx={{ 
                    flex: 1,
                    overflow: 'auto',
                    maxHeight: 'calc(100vh - 300px)', // ensures scrolling works
                    '& .MuiTableCell-root': {
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ 
                                fontWeight: 600, 
                                bgcolor: 'grey.50',
                                width: '40%'
                            }}>
                                Folder Name
                            </TableCell>
                            <TableCell sx={{ 
                                fontWeight: 600, 
                                bgcolor: 'grey.50',
                                width: '60%',
                                minWidth: 280 
                            }}>
                                Permission
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {folders.map(folder => (
                            <TableRow 
                                key={folder.DirectoryId} 
                                sx={{
                                    '&:hover': {
                                        bgcolor: 'rgba(0, 0, 0, 0.07)' // Subtle hover effect
                                    }
                                }}
                            >
                                {/* Folder Name Column */}
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Folder sx={{ color: 'primary.main', fontSize: 20 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {folder.DirectoryName}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                
                                {/* Permission Column - Radio Button Group */}
                                <TableCell>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={folder.Permission || "None"}
                                            onChange={(e) => onPermissionChange(folder.DirectoryId, e.target.value)}
                                            row
                                            sx={{ gap: 1 }}
                                        >
                                            <FormControlLabel 
                                                value="None" 
                                                control={<Radio size="small" />} 
                                                label="None"
                                                sx={{ 
                                                    mr: 2,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                            <FormControlLabel 
                                                value="View" 
                                                control={<Radio size="small" color="info" />} 
                                                label="View"
                                                sx={{ 
                                                    mr: 2,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem',
                                                        color: folder.Permission === "View" ? 'info.main' : 'inherit'
                                                    }
                                                }}
                                            />
                                            <FormControlLabel 
                                                value="Admin" 
                                                control={<Radio size="small" color="warning" />} 
                                                label="Admin"
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem',
                                                        color: folder.Permission === "Admin" ? 'warning.main' : 'inherit'
                                                    }
                                                }}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Empty State */}
            {folders.length === 0 && !loading && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Folder sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        {searchTerm ? 
                            `No folders found matching "${searchTerm}"` : 
                            'No folders available'
                        }
                    </Typography>
                    {searchTerm && (
                        <Button 
                            variant="text" 
                            onClick={() => onSearchChange('')}
                            sx={{ mt: 1 }}
                        >
                            Clear Search
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
}

FolderPermissionsTable.propTypes = {
    folders: array.isRequired,
    loading: bool.isRequired,
    searchTerm: string.isRequired,
    onSearchChange: func.isRequired,
    onPermissionChange: func.isRequired,
    onAllAdmin: func.isRequired,
    onAllView: func.isRequired,
    onClearAll: func.isRequired,
    onUndo: func.isRequired,
    canUndo: bool.isRequired,
    mode: string
};

export default FolderPermissionsTable;
